// The Log week grid: one column per day (Mon–Sun), each column a single day
// card that stacks all of its section cards vertically inside — not a table
// of independent cells. CSS Grid + subgrid (not <table>), mirroring the
// athlete's actual calendar Log grid (LogWeekRow.js / calendar-log.css's
// .log-week-row/.log-day-column/.day-slot): np-log-days sets a shared
// grid-template-rows across all 7 day columns, each np-log-day inherits
// those row tracks via `grid-template-rows: subgrid`, so e.g. every day's
// Nutrition slot lines up on the same row regardless of how tall that day's
// Training slot is — a day with no data for a slot still renders an empty
// np-log-day__slot (not skipped) so the row track stays aligned; visually it
// just reads as blank space, exactly like the reference. The 9
// section bodies are NOT built here — they reuse the athlete-portal's
// existing log-cards
// (js/views/athlete-portal/calendar/components/log-cards/), the same ones
// the athlete's own calendar Log view already ships (dark theme, icons,
// i18n, "View Video", photo carousels — all inherited for free). This file
// only adapts our day/stat/exercises shape into the props those cards
// expect (mostly the near-raw daily-stats sub-documents, confirmed against
// js/views/athlete-portal/calendar/LogView.js, the reference caller).

import React, { useContext } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { RillContext } from "../../../../../rill/RillReact/share/base";
import SvgIcon from "../../../../../parts/SvgIcon";
import { fmtUtc, translateLog } from "./viewTokens";

import LogTrainingCard from "../../../calendar/components/log-cards/LogTrainingCard";
import LogSessionRpeCard from "../../../calendar/components/log-cards/LogSessionRpeCard";
import LogNutritionCard from "../../../calendar/components/log-cards/LogNutritionCard";
import LogFoodLogCard from "../../../calendar/components/log-cards/LogFoodLogCard";
import LogHabitsCard from "../../../calendar/components/log-cards/LogHabitsCard";
import LogReadinessCard from "../../../calendar/components/log-cards/LogReadinessCard";
import LogBodyMeasurementsCard from "../../../calendar/components/log-cards/LogBodyMeasurementsCard";
import LogVitalsCard from "../../../calendar/components/log-cards/LogVitalsCard";
import LogProgressPhotosCard from "../../../calendar/components/log-cards/LogProgressPhotosCard";
import LogMealPhotosCard from "../../../calendar/components/log-cards/LogMealPhotosCard";

// "YYYY-MM-DD" — the dayKey format LogTrainingCard/LogNutritionCard expect
// (see js/views/athlete-portal/calendar/LogView.js, DATE_FORMATS.DATE_DAY_FORMAT).
const dayKeyOf = (utcDate) => moment(String(utcDate), "YYYYMMDD").format("YYYY-MM-DD");

// Extracted so LogView.js's sticky-header clone (useStickyGridHeader) can
// render the exact same date-label + today-badge markup instead of
// duplicating the formatting/translation logic by hand.
export const LogDayHeaderLabel = ({ day, todayUtc }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <>
      <span className="np-log-grid__day-name">{fmtUtc(day.utcDate, "ddd D")}</span>
      {day.utcDate === todayUtc && (
        <span className="np-log-grid__today-badge">{translateLog(t, "today")}</span>
      )}
    </>
  );
};
LogDayHeaderLabel.propTypes = {
  day: PropTypes.shape({ utcDate: PropTypes.number.isRequired }).isRequired,
  todayUtc: PropTypes.number,
};

// .log-card--rest exists in calendar-log.css but had no JS using it yet —
// mirrors PlanTrainingCard's rest-day treatment (same icon + i18n key),
// adapted to the Log card shell instead of the Plan one.
const RestDayCard = () => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className="log-card log-card--rest">
      <div className="log-card__header">
        <SvgIcon src="/img/new-icons/barbell.svg" useCurrentColor={true} />
        <span className="log-card__label">{t("calendar.cards.training.restDay")}</span>
      </div>
    </div>
  );
};

// weekDays.some(...) wrapper — shared by every hasContent below, just to
// drop the repeated ".some" noise at each call site.
const weekHasAny = (weekDays, predicate) => weekDays.some(predicate);

// Builds a section descriptor from ONE hasDayContent check instead of two
// hand-written ones. Previously hasContent (is this category worth a row
// this week?) and the card's own internal null-check (does this specific
// day render anything?) were separate expressions that happened to test the
// same thing — a real drift risk if one changed without the other. Now
// hasDayContent is the single source: render uses it to gate the card,
// hasContent derives the week-level check from it via weekHasAny. This
// still can't see inside the reused cards' own internal logic (e.g.
// LogReadinessCard has its own dailyStat?.readiness?.score ?? ... check) —
// that duplication is unavoidable without touching the 9 shared cards, but
// at least it's not ALSO duplicated here anymore.
const makeSection = (key, hasDayContent, renderCard) => ({
  key,
  hasContent: (weekDays) => weekHasAny(weekDays, hasDayContent),
  render: (day, ctx) => (hasDayContent(day) ? renderCard(day, ctx) : null),
});

// Training is the one exception — it always renders something (a real
// LogTrainingCard or the RestDayCard placeholder), never null, so it skips
// makeSection's hasDayContent gating entirely.
const renderTrainingSection = (day, { prExerciseKeysByDay, repMaxPR }) => {
  if (!day.exercises?.length) return <RestDayCard />;
  const dayKey = dayKeyOf(day.utcDate);
  return (
    <LogTrainingCard
      exercises={day.exercises}
      dayKey={dayKey}
      prExerciseKeys={prExerciseKeysByDay[dayKey] || new Set()}
      repMaxPR={repMaxPR}
    />
  );
};
// LogSessionRpeCard self-guards (returns null) on sessionRPE == null || <= 0
// — same shape check duplicated here so hasContent can gate the week-level
// row without rendering every card first (see makeSection's own comment on
// why this duplication is unavoidable without touching the shared card).
const hasSessionRpeContent = (day) => (day.stat?.sessionRPE ?? 0) > 0;
// Renders the Session RPE Card
const renderSessionRpeCard = (day) => <LogSessionRpeCard dailyStat={day.stat} />;

// Checks if the nutrition content is present
const hasNutritionContent = (day) => {
  const c = day.row?.consumed;
  return !!(day.row?.target || (c && (c.kcal || c.proteins || c.carbs || c.fats)));
};
// Renders the Nutrition Card
const renderNutritionCard = (day, { nutritionPlans }) => (
  <LogNutritionCard
    nutritionSummary={day.row?.consumed ? { nutrients: day.row.consumed } : null}
    nutritionPlans={nutritionPlans}
    dayKey={dayKeyOf(day.utcDate)}
    exercises={day.exercises}
  />
);
// Checks if the food log content is present
const hasFoodLogContent = (day) =>
  (day.stat?.foodLog?.meals?.length ?? 0) > 0 ||
  (day.stat?.internalFoodLog?.meals?.length ?? 0) > 0;
// Renders the Food Log Card
const renderFoodLogCard = (day) => <LogFoodLogCard foodLogs={day.stat ? [day.stat] : []} />;

// day.habitLog (not day.stat) — the resolved {utcDate, accountabilities:
// [{_id, title, done}]} from the habitLogs loader (coach/org template +
// DailyStat.accountabilities merge, msb-api/lib/api/habit-logs.js). The raw
// daily-stats accountabilities array alone lacks title/description.
const hasHabitsContent = (day) => (day.habitLog?.accountabilities?.length ?? 0) > 0;
// Renders the Habits Card
const renderHabitsCard = (day) => <LogHabitsCard habitLogs={day.habitLog} />;

// Checks if the readiness content is present
const hasReadinessContent = (day) => {
  const s = day.stat?.readiness?.score ?? day.stat?.readiness;
  return !!s || s === 0;
};
// Renders the Readiness Card
const renderReadinessCard = (day) => <LogReadinessCard dailyStat={day.stat} />;

// "measurements" (not "bodyMeasurements") — matches LOG_CATEGORY_KEYS /
// the colored calendar-filter-pill[data-category] rules in calendar-log.css.
const hasMeasurementsContent = (day) =>
  Object.keys(day.stat?.bodyMeasurements ?? {}).length > 0;
// Renders the Measurements Card
const renderMeasurementsCard = (day, { units }) => (
  <LogBodyMeasurementsCard bodyMeasurements={day.stat} units={units} />
);

// Checks if the vitals content is present
const hasVitalsContent = (day) => !!day.stat;
// Renders the Vitals Card
const renderVitalsCard = (day, { units }) => (
  <LogVitalsCard dailyStat={day.stat} units={units} />
);

// "photos" (not "progressPhotos") — same reasoning as "measurements" above.
const hasPhotosContent = (day) =>
  Object.values(day.stat?.bodyPhotos ?? {}).some((v) => v?.s3Path);
// dailyStats' withThreads:true (our loader always sends it) makes msb-api
// strip the raw bodyPhotos.<angle>.threadId field and replace it with a full
// .thread object instead (msb-api/lib/api_helpers/threads-pipelines.js —
// "we now have the full thread object"). LogProgressPhotosCard reads
// photo.threadId directly, so back-fill it from .thread._id here rather than
// touching that shared, reused-as-is component.
const withBackfilledThreadIds = (stat) => {
  const bodyPhotos = stat?.bodyPhotos;
  if (!bodyPhotos) return stat;
  return {
    ...stat,
    bodyPhotos: Object.fromEntries(
      Object.entries(bodyPhotos).map(([angle, photo]) => [
        angle,
        { ...photo, threadId: photo.threadId ?? photo.thread?._id },
      ])
    ),
  };
};
// Renders the Photos Card
const renderPhotosCard = (day) => (
  <LogProgressPhotosCard
    progressPhotos={withBackfilledThreadIds(day.stat)}
    dailyStatId={day.stat?._id}
    utcDate={day.utcDate}
  />
);
// Checks if the meal photos content is present
const hasMealPhotosContent = (day) =>
  Object.values(day.stat?.mealPhotos ?? {}).some((meal) => (meal?.list?.length ?? 0) > 0);
// Renders the Meal Photos Card
const renderMealPhotosCard = (day, { locals }) => (
  <LogMealPhotosCard
    mealPhotos={day.stat}
    dailyStatId={day.stat?._id}
    utcDate={day.utcDate}
    locals={locals}
  />
);

// One row per section, fixed order from the design — sessionRpe sits right
// after training, same position the athlete's own Calendar Log uses
// (LogWeekRow.js), so the row order matches for a coach who uses both.
const SECTIONS = [
  { key: "training", render: renderTrainingSection, hasContent: () => true },
  makeSection("sessionRpe", hasSessionRpeContent, renderSessionRpeCard),
  makeSection("nutrition", hasNutritionContent, renderNutritionCard),
  makeSection("foodLog", hasFoodLogContent, renderFoodLogCard),
  makeSection("habits", hasHabitsContent, renderHabitsCard),
  makeSection("readiness", hasReadinessContent, renderReadinessCard),
  makeSection("measurements", hasMeasurementsContent, renderMeasurementsCard),
  makeSection("vitals", hasVitalsContent, renderVitalsCard),
  makeSection("photos", hasPhotosContent, renderPhotosCard),
  makeSection("mealPhotos", hasMealPhotosContent, renderMealPhotosCard),
];

// The whole card opens the Day Drawer, but the card also contains real
// nested interactive controls we don't own (View Video, photo carousels —
// calendar/'s log-cards). Native click bubbling means our handler on the
// card sees every click regardless of where inside it originated, so we
// don't need those children to cooperate (no stopPropagation, no touching
// calendar/) — we just check what was actually clicked: if it's an
// interactive element OTHER than the card itself, let it do its own thing
// and skip opening the drawer.
const isNestedInteractiveClick = (e) => {
  const interactive = e.target.closest('button, a, [role="button"], input, select, textarea');
  return interactive != null && interactive !== e.currentTarget;
};

// Shared by onClick/onKeyDown below — each handler only does its own
// gating (mouse needs none, keyboard needs the Enter/Space check) before
// deferring here for the actual interactive-descendant check + dispatch.
const fireIfNotNestedInteractive = (e, day, onDayClick) => {
  if (isNestedInteractiveClick(e)) return;
  onDayClick?.(day);
};

export const LogGrid = ({
  weekDays,
  todayUtc,
  nutritionPlans,
  units,
  prExerciseKeysByDay = {},
  repMaxPR,
  activeFilters,
  gridRef,
  onDayClick,
}) => {
  const { locals } = useContext(RillContext);
  const ctx = { nutritionPlans, units, locals, prExerciseKeysByDay, repMaxPR };
  // No activeFilters (or null) = every category passes the user-toggle
  // check, same convention as LogWeekRow's own `show = (key) =>
  // !activeFilters || ...`. hasContent(weekDays) is the second, AND'd gate —
  // a category only gets a row this week if some day actually has data for
  // it, mirroring LogWeekRow.js's `activeSlots = slotDefs.filter((s) =>
  // show(s.key) && s.hasContent())`.
  // Out-of-range days render only their date header (see below), so they must
  // not count toward hasContent either — otherwise a row would appear for a
  // section only an out-of-range day has data for.
  const inRangeDays = weekDays.filter((day) => !day.outOfRange);
  const activeSections = SECTIONS.filter(
    ({ key, hasContent }) =>
      (!activeFilters || activeFilters.includes(key)) && hasContent(inRangeDays)
  );

  // +1 row track for the date slot, same as LogWeekRow.js's
  // `activeSlots.length + 1` — sets the shared row budget every day column's
  // subgrid inherits.
  return (
    <div
      ref={gridRef}
      className="np-log-days"
      style={{ gridTemplateRows: `repeat(${activeSections.length + 1}, auto)` }}
    >
      {weekDays.map((day) => {
        // Days outside the selected range / nutrition plan keep their column
        // (the grid, week label and sticky header all assume 7) but show only
        // the date: dimmed, no section data, not clickable.
        if (day.outOfRange) {
          return (
            <div key={day.utcDate} className="np-log-day np-log-day--out-range">
              <div className="np-log-day__slot np-log-day__date">
                <LogDayHeaderLabel day={day} todayUtc={todayUtc} />
              </div>
            </div>
          );
        }
        return (
          // Contains real nested interactive controls (View Video, photo
          // carousels) reused from calendar/log-cards — can't be a real
          // <button> (invalid to nest interactive content inside one), see
          // isNestedInteractiveClick above for how clicks on those still
          // resolve correctly without touching that folder.
          // biome-ignore lint/a11y/useSemanticElements: contains real nested interactive content, can't be a real button
          <div // NOSONAR
            key={day.utcDate}
            className={`np-log-day${day.utcDate === todayUtc ? " np-log-day--today" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={moment(String(day.utcDate), "YYYYMMDD").format("MMM D, YYYY")}
            onClick={(e) => fireIfNotNestedInteractive(e, day, onDayClick)}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              fireIfNotNestedInteractive(e, day, onDayClick);
            }}
          >
            <div className="np-log-day__slot np-log-day__date">
              <LogDayHeaderLabel day={day} todayUtc={todayUtc} />
            </div>
            {activeSections.map(({ key, render }) => (
              <div key={key} className="np-log-day__slot">
                {render(day, ctx)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const weekDayShape = PropTypes.shape({
  utcDate: PropTypes.number.isRequired,
  row: PropTypes.object,
  stat: PropTypes.object,
  exercises: PropTypes.array,
  habitLog: PropTypes.object,
  outOfRange: PropTypes.bool,
});

LogGrid.propTypes = {
  weekDays: PropTypes.arrayOf(weekDayShape).isRequired,
  todayUtc: PropTypes.number,
  nutritionPlans: PropTypes.arrayOf(PropTypes.object),
  units: PropTypes.string,
  prExerciseKeysByDay: PropTypes.object,
  repMaxPR: PropTypes.object,
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  gridRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onDayClick: PropTypes.func,
};

export default LogGrid;
